import {
    ElementType, useRef, DragEvent,
    ChangeEvent, useState, useEffect, useCallback
} from 'react';
import { Box, Stack, Typography, Button as MuiButton } from '@mui/material';
import { Paper } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useTheme } from "@mui/material";
import { ArrowBack, } from '@mui/icons-material';
import { Button } from '@/components/Button';
import { Notification } from '@/components/Notification';

interface UploadProps {
    isLoading?: string;
    error?: string;
    clearError: () => void;
    setUploading: (uploading: boolean) => void;
    createDocument: (file: File) => Promise<boolean>;
}

export const Upload: ElementType<UploadProps> = (
    { isLoading, error, clearError, setUploading, createDocument }) => {

    const theme = useTheme();
    const [dragging, setDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleDrag = useCallback(
        (e: DragEvent<HTMLDivElement>, dragOver: boolean) => {
            e.preventDefault();
            e.stopPropagation();
            setDragging(dragOver);
        }, [setDragging]);

    const handleFileSelect = useCallback(
        (e: DragEvent<HTMLDivElement> | ChangeEvent<HTMLInputElement>) => {
            e.preventDefault();
            setDragging(false);

            let files: FileList | null;
            if (e.type === 'drop') {
                files = (e as DragEvent<HTMLDivElement>).dataTransfer.files;
            } else {
                files = (e.target as HTMLInputElement).files;
            }

            if (files && files.length > 0) {
                createDocument(files[0]);
            }

            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }, [createDocument, setDragging, fileInputRef]);

    useEffect(() => {
        if (!fileInputRef.current) return;
    }, [fileInputRef.current]);

    return (
        <Stack padding={theme.spacing(3)}>
            <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-end',
                gap: 1,
            }}>
                <Button
                    variant="secondary"
                    icon={<ArrowBack />}
                    tooltip="Back to documents"
                    onClick={() => setUploading(false)}
                />
            </Box>

            <Box
                display="flex"
                justifyContent="center"
                flex={1}
                padding={theme.spacing(4)}
            >
                <Paper
                    onDragEnter={(e) => handleDrag(e, true)}
                    onDragLeave={(e) => handleDrag(e, false)}
                    onDragOver={(e) => handleDrag(e, true)}
                    onDrop={handleFileSelect}
                    elevation={3}
                    sx={{
                        border: dragging ? '2px dashed #000' : '2px dashed #aaa',
                        width: '68%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        p: theme.spacing(4)
                    }}>
                    <CloudUploadIcon
                        sx={{
                            fontSize: '42px',
                            marginBottom: '20px'
                        }} />
                    <Typography variant="body1" sx={{
                        width: '1',
                        textAlign: 'center'
                    }}>
                        {dragging ?
                            "Drop here..." :
                            "Drag 'n' drop a file here, or click to select a file"
                        }
                    </Typography>
                    <MuiButton
                        variant="contained"
                        component="label"
                        style={{ marginTop: 20 }}
                    >
                        Upload File
                        <input
                            type="file"
                            ref={fileInputRef}
                            hidden
                            onChange={handleFileSelect}
                            multiple={false}
                        />
                    </MuiButton>
                </Paper>
            </Box>
            <Notification
                isLoading={isLoading}
                error={error}
                delay={300}
                autoHideDuration={1200}
                clearError={clearError}
            />
        </Stack>
    );
};
